// React-SVG-Bausteine für die interaktiven Vorschauen (Editor-Look).
// Reine Funktionen, keine externe Library. Farben aus dem Claude-Design (HSL).
import React from "react";

// Grundfarben (Wand, Boden, Engstelle, Maßlinien).
export const C = {
  wall: "hsl(36 25% 94%)",
  wallStroke: "hsl(30 14% 78%)",
  obj: "hsl(188 72% 28% / 0.10)",
  objStroke: "hsl(188 72% 28%)",
  dim: "hsl(215 12% 42%)",
  floor: "hsl(30 14% 64%)",
  primary: "hsl(188 72% 28%)",
  guide: "hsl(215 12% 70%)",
  center: "hsl(215 12% 55%)",
};

// Ergebnis-Farbsätze (passt / knapp / nicht / unsicher) — färbt das Möbelstück ein.
export const ST = {
  fits: { stroke: "hsl(150 58% 33%)", fill: "hsl(150 55% 38% / 0.16)", text: "hsl(150 55% 24%)", bg: "hsl(150 55% 42% / 0.12)", border: "hsl(150 46% 42% / 0.5)", dot: "hsl(150 58% 37%)" },
  tight: { stroke: "hsl(36 84% 42%)", fill: "hsl(40 92% 52% / 0.18)", text: "hsl(28 76% 32%)", bg: "hsl(40 90% 52% / 0.14)", border: "hsl(38 80% 48% / 0.55)", dot: "hsl(38 88% 47%)" },
  no: { stroke: "hsl(2 70% 48%)", fill: "hsl(2 74% 54% / 0.14)", text: "hsl(2 64% 40%)", bg: "hsl(2 74% 54% / 0.1)", border: "hsl(2 68% 52% / 0.45)", dot: "hsl(2 72% 50%)" },
  uncertain: { stroke: "hsl(215 14% 44%)", fill: "hsl(215 16% 50% / 0.14)", text: "hsl(215 18% 30%)", bg: "hsl(215 14% 50% / 0.1)", border: "hsl(215 12% 52% / 0.45)", dot: "hsl(215 14% 46%)" },
};

// calc.js liefert "does_not_fit" — auf den kompakten ST-Schlüssel mappen.
export function stKey(status) {
  return status === "does_not_fit" ? "no" : status;
}

const FONT = "Inter, sans-serif";

function fmtN(n) {
  const r = Math.round(n * 10) / 10;
  return (Number.isInteger(r) ? r.toString() : r.toFixed(1)).replace(".", ",");
}

// Maßangabe als kleine Pille (weiß mit farbigem Rand), in der viewBox geclamped.
export function pill(key, x, y, text, color = C.dim, anchor = "middle", vbW = 880) {
  const w = String(text).length * 7.2 + 16;
  const h = 22;
  let bx = x - (anchor === "end" ? w : anchor === "start" ? 0 : w / 2);
  bx = Math.max(6, Math.min(bx, vbW - 6 - w));
  return (
    <g key={key}>
      <rect x={bx} y={y - h / 2} width={w} height={h} rx={6} fill="#fff" stroke={color} strokeWidth={1.2} />
      <text x={bx + w / 2} y={y + 0.5} fill={color} fontSize={13} fontWeight={600} textAnchor="middle" dominantBaseline="middle" fontFamily={FONT} style={{ fontVariantNumeric: "tabular-nums" }}>
        {text}
      </text>
    </g>
  );
}

// Horizontale Maßlinie mit Endkappen und optionaler Pille.
export function hDim(key, x1, x2, y, label, color = C.dim, vbW = 880) {
  return (
    <g key={key}>
      <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth={1.4} />
      <line x1={x1} y1={y - 5} x2={x1} y2={y + 5} stroke={color} strokeWidth={1.4} />
      <line x1={x2} y1={y - 5} x2={x2} y2={y + 5} stroke={color} strokeWidth={1.4} />
      {label != null ? pill(key + "p", (x1 + x2) / 2, y, label, color, "middle", vbW) : null}
    </g>
  );
}

// Vertikale Maßlinie.
export function vDim(key, y1, y2, x, label, color = C.dim, vbW = 880) {
  return (
    <g key={key}>
      <line x1={x} y1={y1} x2={x} y2={y2} stroke={color} strokeWidth={1.4} />
      <line x1={x - 5} y1={y1} x2={x + 5} y2={y1} stroke={color} strokeWidth={1.4} />
      <line x1={x - 5} y1={y2} x2={x + 5} y2={y2} stroke={color} strokeWidth={1.4} />
      {label != null ? pill(key + "p", x, (y1 + y2) / 2, label, color, "middle", vbW) : null}
    </g>
  );
}

// Anfasser zum Ziehen (Kreis mit Doppelstrich). hit = unsichtbare Trefferfläche.
export function handle(key, cx, cy, cursor, onDown, orient = "h") {
  const hit = 34;
  const r = 12;
  const lines =
    orient === "v"
      ? [
          [cx - 4, cy - 5, cx - 4, cy + 5],
          [cx + 4, cy - 5, cx + 4, cy + 5],
        ]
      : [
          [cx - 5, cy - 4, cx + 5, cy - 4],
          [cx - 5, cy + 4, cx + 5, cy + 4],
        ];
  return (
    <g key={key} style={{ cursor }} onPointerDown={onDown}>
      <rect x={cx - hit / 2} y={cy - hit / 2} width={hit} height={hit} fill="transparent" />
      <circle cx={cx} cy={cy} r={r} fill="#fff" stroke={C.primary} strokeWidth={2.5} />
      {lines.map((l, i) => (
        <line key={i} x1={l[0]} y1={l[1]} x2={l[2]} y2={l[3]} stroke={C.primary} strokeWidth={2} strokeLinecap="round" />
      ))}
    </g>
  );
}

// Beschriftung im Möbel-Querschnitt (z. B. "Querschnitt", "Draufsicht").
export function fpLabel(key, cx, cy, color, text) {
  return (
    <text key={key} x={cx} y={cy + 5} fill={color} fontSize={14} fontWeight={600} textAnchor="middle" fontFamily={FONT} style={{ pointerEvents: "none" }}>
      {text}
    </text>
  );
}

// SVG-Wrapper mit fester viewBox, touch-action none (für sauberes Drag auf Mobile).
export function Svg({ vbW, vbH, children, ariaLabel }) {
  return (
    <svg
      viewBox={`0 0 ${vbW} ${vbH}`}
      width="100%"
      role="img"
      aria-label={ariaLabel || "Interaktive Vorschau: passt das Möbelstück durch die Engstelle?"}
      style={{ width: "100%", height: "auto", display: "block", touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
    >
      {children}
    </svg>
  );
}

// Platzhalter-Canvas, solange Maße fehlen.
export function placeholder(msg) {
  return (
    <Svg vbW={880} vbH={470}>
      <text x={440} y={235} fill={C.dim} fontSize={16} textAnchor="middle" fontFamily={FONT}>
        {msg}
      </text>
    </Svg>
  );
}

export { fmtN };
